// Finds EF Core migration sets from a file listing alone (names only, no contents), so a large
// repository or a remote GitHub tree can be scanned instantly. Contents are read lazily later.

export interface SourceFileEntry {
  /** Path relative to the source root, with `/` separators. */
  path: string;
  size?: number;
  lastModified?: number;
}

export interface MigrationEntry {
  /** `20240101120000_Initial` */
  id: string;
  timestamp: string;
  name: string;
  mainPath: string | null;
  designerPath: string | null;
}

export interface MigrationSetInfo {
  /** Stable key: the snapshot path, or the folder when there is no snapshot. */
  key: string;
  folder: string;
  snapshotPath: string | null;
  /** From the snapshot file name (`BloggingContextModelSnapshot.cs` gives `BloggingContext`). */
  contextName: string;
  migrations: MigrationEntry[];
  /** Nearest `.csproj` above the folder. */
  projectPath: string | null;
}

export interface DiscoveryResult {
  sets: MigrationSetInfo[];
  /**
   * Migrations in a folder shared by several snapshots. Their `[DbContext]` attribute decides the set;
   * see assignMigration.
   */
  unassigned: { migration: MigrationEntry; candidates: string[] }[];
  /** Files under `.modelviewer/` (agent-written sketches and notes). */
  workspaceFiles: string[];
  projects: string[];
}

/** Directory names never scanned. */
export const IGNORED_DIRECTORIES = new Set(['bin', 'obj', 'node_modules', '.git', '.vs', '.idea', 'TestResults']);

const MIGRATION_FILE = /^(\d{14})_(.+?)(\.Designer)?\.cs$/;
const SNAPSHOT_FILE = /^(.+)ModelSnapshot\.cs$/;

export function isIgnoredPath(path: string): boolean {
  return path.split('/').some((segment) => IGNORED_DIRECTORIES.has(segment));
}

/**
 * Whether a folder with these entries is a git checkout: `.git` is a folder in a clone and a file in
 * a linked worktree or submodule. Listing skips a checkout inside another one (Claude Code keeps
 * worktrees in `.claude/worktrees/`): it holds another copy of the code, often at another commit,
 * so its contexts would show up twice. A folder of separate repositories still lists them all.
 */
export function isCheckout(entryNames: readonly string[]): boolean {
  return entryNames.includes('.git');
}

/** Drops paths inside nested checkouts (see isCheckout), for sources that list every file at once. */
export function withoutNestedCheckouts(paths: string[]): string[] {
  const checkouts = new Set<string>();
  for (const path of paths) {
    const segments = path.split('/');
    const git = segments.indexOf('.git');
    if (git >= 0) checkouts.add(segments.slice(0, git).join('/'));
  }
  const nested = [...checkouts].filter((dir) => dir !== '' && ancestors(dirname(dir)).some((a) => checkouts.has(a)));
  if (nested.length === 0) return paths;
  return paths.filter((path) => !nested.some((dir) => path.startsWith(`${dir}/`)));
}

function dirname(path: string): string {
  const i = path.lastIndexOf('/');
  return i < 0 ? '' : path.slice(0, i);
}

function basename(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

function ancestors(dir: string): string[] {
  const out = [dir];
  let d = dir;
  while (d !== '') {
    d = dirname(d);
    out.push(d);
  }
  return out;
}

export function discoverMigrationSets(entries: SourceFileEntry[]): DiscoveryResult {
  const snapshotsByDir = new Map<string, string[]>();
  const migrationsByDir = new Map<string, Map<string, MigrationEntry>>();
  const workspaceFiles: string[] = [];
  const projects: string[] = [];

  for (const { path } of entries) {
    if (path.startsWith('.modelviewer/') || path.includes('/.modelviewer/')) {
      workspaceFiles.push(path);
      continue;
    }
    if (isIgnoredPath(path)) continue;
    const name = basename(path);
    if (name.endsWith('.csproj')) {
      projects.push(path);
      continue;
    }
    if (!name.endsWith('.cs')) continue;
    const dir = dirname(path);
    const snap = SNAPSHOT_FILE.exec(name);
    if (snap) {
      const list = snapshotsByDir.get(dir) ?? [];
      list.push(path);
      snapshotsByDir.set(dir, list);
      continue;
    }
    const mig = MIGRATION_FILE.exec(name);
    if (mig) {
      const [, timestamp, migName, designer] = mig as unknown as [string, string, string, string | undefined];
      const id = `${timestamp}_${migName}`;
      const byId = migrationsByDir.get(dir) ?? new Map<string, MigrationEntry>();
      const entry = byId.get(id) ?? { id, timestamp, name: migName, mainPath: null, designerPath: null };
      if (designer) entry.designerPath = path;
      else entry.mainPath = path;
      byId.set(id, entry);
      migrationsByDir.set(dir, byId);
    }
  }

  const sets = new Map<string, MigrationSetInfo>();
  const nearestProject = (dir: string): string | null => {
    for (const a of ancestors(dir)) {
      const found = projects.filter((p) => dirname(p) === a).sort()[0];
      if (found) return found;
    }
    return null;
  };
  for (const [dir, snapshots] of snapshotsByDir) {
    for (const snapshotPath of snapshots) {
      const contextName = SNAPSHOT_FILE.exec(basename(snapshotPath))![1]!;
      sets.set(snapshotPath, { key: snapshotPath, folder: dir, snapshotPath, contextName, migrations: [], projectPath: nearestProject(dir) });
    }
  }

  const unassigned: DiscoveryResult['unassigned'] = [];
  for (const [dir, byId] of migrationsByDir) {
    // Migrations belong to the snapshot in the same folder or the nearest folder above.
    const home = ancestors(dir).find((a) => snapshotsByDir.has(a));
    const candidates = home !== undefined ? snapshotsByDir.get(home)! : [];
    for (const migration of byId.values()) {
      if (candidates.length === 1) {
        sets.get(candidates[0]!)!.migrations.push(migration);
      } else if (candidates.length > 1) {
        unassigned.push({ migration, candidates: [...candidates] });
      } else {
        let orphan = sets.get(dir);
        if (!orphan) {
          orphan = { key: dir, folder: dir, snapshotPath: null, contextName: basename(dir) || '(root)', migrations: [], projectPath: nearestProject(dir) };
          sets.set(dir, orphan);
        }
        orphan.migrations.push(migration);
      }
    }
  }

  const ordinal = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  for (const set of sets.values()) set.migrations.sort((a, b) => ordinal(a.id, b.id));
  return {
    sets: [...sets.values()].sort((a, b) => ordinal(a.folder, b.folder) || ordinal(a.contextName, b.contextName)),
    unassigned,
    workspaceFiles: workspaceFiles.sort(),
    projects: projects.sort(),
  };
}

/**
 * Places an unassigned migration once its Designer file's `[DbContext(typeof(X))]` is known.
 * Returns false if no candidate snapshot matches the context name.
 */
export function assignMigration(result: DiscoveryResult, migrationId: string, contextType: string): boolean {
  const index = result.unassigned.findIndex((u) => u.migration.id === migrationId);
  if (index < 0) return false;
  const shortName = contextType.slice(contextType.lastIndexOf('.') + 1);
  const pending = result.unassigned[index]!;
  const set = result.sets.find((s) => pending.candidates.includes(s.key) && s.contextName === shortName);
  if (!set) return false;
  set.migrations.push(pending.migration);
  set.migrations.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  result.unassigned.splice(index, 1);
  return true;
}
